/**
 * Pure achievement rules. The FIRST, deliberately-basic set: three score
 * milestones a run can unlock (score = survival time × {@link
 * import('./constants').SCORE.perSecond}). The SERVER is the source of truth —
 * the Cloud Function awards these on a validated `submitRun` (a client never
 * grants its own); this module only defines the catalog + the pure
 * "which milestones does this score clear" maths, shared by the server (to
 * award), both data backends (offline parity), and the UI (to draw the wall).
 *
 * Tracking "how many players earned each" is a NETWORK aggregate
 * (`stats/{gameId}.achievementCounts`) the Function bumps the first time a given
 * player unlocks a given achievement — see {@link
 * import('../services/dataModel').NetworkStats}.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here. Adding an
 * achievement here is safe (no SIM_VERSION impact): achievements read the score,
 * not the simulation.
 */

/** The stable ids of the three basic score achievements (owner-specced thresholds). */
export type AchievementId = 'survivor' | 'veteran' | 'legend';

/** One achievement's immutable catalog metadata. */
export interface AchievementDef {
  id: AchievementId;
  /** Short display title. */
  name: string;
  /** One-line description shown under the title. */
  desc: string;
  /** A glyph for the badge (placeholder until art lands). */
  icon: string;
  /**
   * The authoritative score a single run must REACH (≥) to unlock it. Score is a
   * clean 1 point per second survived ({@link import('./constants').SCORE.perSecond}),
   * so these thresholds ARE the survival times in seconds: 25s / 75s / 150s.
   */
  scoreThreshold: number;
}

/**
 * The three basic achievements, ascending by threshold. Owner-mandated survival
 * milestones: 25 / 75 / 150 (seconds, since score is 1 point/second).
 */
export const ACHIEVEMENTS: readonly AchievementDef[] = [
  {
    id: 'survivor',
    name: 'Survivor',
    desc: 'Survive 25 seconds in a single run.',
    icon: '🛡️',
    scoreThreshold: 25,
  },
  {
    id: 'veteran',
    name: 'Veteran',
    desc: 'Survive 75 seconds in a single run.',
    icon: '🎖️',
    scoreThreshold: 75,
  },
  {
    id: 'legend',
    name: 'Legend',
    desc: 'Survive 150 seconds in a single run.',
    icon: '👑',
    scoreThreshold: 150,
  },
] as const;

/** The ordered list of achievement ids (handy for stable iteration / typing). */
export const ACHIEVEMENT_IDS: readonly AchievementId[] = ACHIEVEMENTS.map((a) => a.id);

/** Look up an achievement definition by id (or undefined if unknown). */
export function achievementById(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}

/**
 * Every achievement a run of the given score CLEARS (threshold ≤ score), as ids.
 * Order matches {@link ACHIEVEMENTS} (ascending threshold).
 *
 * @param score the authoritative run score.
 */
export function achievementsForScore(score: number): AchievementId[] {
  const s = Number.isFinite(score) ? score : 0;
  return ACHIEVEMENTS.filter((a) => s >= a.scoreThreshold).map((a) => a.id);
}

/**
 * The achievements a run of `score` unlocks that the player does NOT already own —
 * i.e. the *newly* unlocked ids. Used by the server (to grant + bump the network
 * counter) and the game-over UI (to celebrate). Stable, de-duplicated, ascending.
 *
 * @param score the authoritative run score.
 * @param owned the player's already-unlocked achievement ids.
 */
export function newlyUnlockedAchievements(
  score: number,
  owned: readonly string[],
): AchievementId[] {
  const have = new Set(owned);
  return achievementsForScore(score).filter((id) => !have.has(id));
}
